const BaseView = require('../../components/common/common-component');
const EditorTemplate = require('./editor.hbs');
const StopsListTemplate = require('./stops-list.hbs');
import GradientHelpers from '../../../../gradients/gradient-helpers';
import LinearGradient from '../../../../gradients/linear-gradient';
import RadialGradient from '../../../../gradients/radial-gradient';

module.exports = BaseView.extend({
  screenSize: 256, // SVG width and height for gradient direction editor
  padding: 32, // Screen padding for gradient direction editor
  maxStopsCount: 5, // Maximum number of stops a gradient can have

  events: {
    ...BaseView.prototype.events,
    'click #btn-apply': 'onApplyToSelection',
    'click #btn-duplicate': 'onDuplicateGradient',
    'click #btn-convert': 'onConvertGradient',
    'click #btn-delete': 'onDeleteGradient',
    'click #btn-close': 'onCloseEditor',
    'mousedown .gradients-editor-dialog svg': 'onSvgMouseDown',
    'mousemove .gradients-editor-dialog svg': 'onSvgMouseMove',
    'mouseup .gradients-editor-dialog svg': 'onSvgMouseUp',
    'mouseleave .gradients-editor-dialog svg': 'onSvgMouseLeave',
  },

  show: function () {
    BaseView.prototype.initialize.call(this);
    this.render();
  },

  render: function () {
    const isLinear = this.isLinear;
    const isRadial = !this.isLinear;
    this.$el.html(EditorTemplate({ gradientId: this.gradientId, isLinear, isRadial }));

    const gradientElement = GradientHelpers.getGradientById(this.gradientId);
    if (isLinear) {
      this.renderLinear(gradientElement)
    } else {
      this.renderGradient(gradientElement);
    }
  },

  renderLinear: function (gradientElement) {
    // Object representing the linearGradient SVG element
    const linearGradient = new LinearGradient(gradientElement);
    // Update gradient direction editor (for start and end points)
    this.updateGradientDirectionUi(linearGradient.x1, linearGradient.y1, false);
    this.updateGradientDirectionUi(linearGradient.x2, linearGradient.y2, true);

    // Update gradient stops editor
    this.updateGradientStopsUi(linearGradient);
  },

  renderGradient: function (gradientElement) {
    // Object representing the radialGradient SVG element
    const radialGradient = new RadialGradient(gradientElement);
    this.updateGradientCenterUi(radialGradient.cx, radialGradient.cy);
    // Update gradient stops editor
    this.updateGradientStopsUi(radialGradient);
  },

  updateGradientStopsUi: function (gradient) {
    // Get the element to place UI
    const stopsListElement = this.$el.find('.gradients-editor-stops');
    // Don't allow too many gradient stops
    const addStopDisabled = gradient.stops.length >= this.maxStopsCount;
    // Don't allow user delete stops if the count drops below 2
    const deleteButtonClass = gradient.stops.length <= 2 ? '-disabled' : '';
    const stops = gradient.stops;
    stopsListElement.html(StopsListTemplate({
      stops,
      addStopDisabled,
      deleteButtonClass
    }));
    // Add event listeners
    stopsListElement.find('input').on('change', this.onStopsChanged.bind(this));
    stopsListElement.find('#btn-add-stop').on('click', this.onAddStop.bind(this));
    stopsListElement.find('.delete-stop').on('click', this.onDeleteStop.bind(this));
  },

  updateGradientDirectionUi: function (x, y, end) {
    const targetX = this.padding + (this.screenSize - 2 * this.padding) * x / 100;
    const targetY = this.padding + (this.screenSize - 2 * this.padding) * y / 100;

    const $circle = this.$el.find('.gradients-editor-dialog circle#' + (end ? 'end' : 'start'));
    $circle.attr('cx', targetX);
    $circle.attr('cy', targetY);

    const $line = this.$el.find('.gradients-editor-dialog line');
    $line.attr(end ? 'x2' : 'x1', targetX);
    $line.attr(end ? 'y2' : 'y1', targetY);
  },

  updateGradientDirection: function (x, y, end) {
    const gradientElement = GradientHelpers.getGradientById(this.gradientId);
    const linearGradient = new LinearGradient(gradientElement);
    if (end) {
      linearGradient.x2 = x;
      linearGradient.y2 = y;
    } else {
      linearGradient.x1 = x;
      linearGradient.y1 = y;
    }
    linearGradient.toElement(gradientElement);
  },

  updateGradientCenterUi: function (x, y) {
    const gradientElement = GradientHelpers.getGradientById(this.gradientId);
    const targetX = this.padding + (this.screenSize - 2 * this.padding) * x / 100;
    const targetY = this.padding + (this.screenSize - 2 * this.padding) * y / 100;

    const $circle = this.$el.find('.gradients-editor-dialog circle#center');
    $circle.attr('cx', targetX);
    $circle.attr('cy', targetY);
  },

  updateGradientCenter: function (x, y) {
    const gradientElement = GradientHelpers.getGradientById(this.gradientId);
    const radialGradient = new RadialGradient(gradientElement);
    radialGradient.cx = x;
    radialGradient.cy = y;
    radialGradient.toElement(gradientElement);
  },

  onAddStop: function () {
    const gradientElement = GradientHelpers.getGradientById(this.gradientId);
    const gradient = this.isLinear ?
      new LinearGradient(gradientElement) :
      new RadialGradient(gradientElement);

    if (gradient.stops.length >= this.maxStopsCount) return;
    GradientHelpers.addNewStop(gradient);
    // Update SVG
    const newStopElement = gradientElement.lastChild.cloneNode(false);
    gradientElement.appendChild(newStopElement);
    gradient.stops.forEach((stop, i) => {
      stop.toElement(gradientElement.childNodes[i]);
    });
    this.updateGradientStopsUi(gradient);
  },

  onDeleteStop: function (event) {
    const index = event.target.getAttribute('data-index');
    const gradientElement = GradientHelpers.getGradientById(this.gradientId);
    const gradient = this.isLinear ?
      new LinearGradient(gradientElement) :
      new RadialGradient(gradientElement);
    if (gradient.stops.length <= 2) return;
    GradientHelpers.deleteStop(gradient, index);
    // Update SVG
    gradientElement.removeChild(gradientElement.lastChild);
    gradient.stops.forEach((stop, i) => {
      stop.toElement(gradientElement.childNodes[i]);
    });
    this.updateGradientStopsUi(gradient);
  },

  onApplyToSelection: function () {
    GradientHelpers.applyGradientToSelection(this.gradientId);
  },

  onDuplicateGradient: function () {
    GradientHelpers.duplicateGradientElement(this.gradientId);
    this.onCloseEditor(true);
  },

  onConvertGradient: function () {
    GradientHelpers.convertGradientElement(this.gradientId);
    this.onCloseEditor(true);
  },

  onDeleteGradient: function () {
    if (GradientHelpers.isUsed(this.gradientId)) {
      alert('This gradient is used in the canvas.');
      return;
    }

    GradientHelpers.deleteGradientElement(this.gradientId);
    this.onCloseEditor(true);
  },

  onCloseEditor: function () {
    this.onCloseEditor(false);
  },

  onStopsChanged: function (event) {
    // Index of modified stop
    const index = event.target.getAttribute('data-index');
    // Which property changed
    const propertyType = event.target.getAttribute('data-type');
    // Gradient SVG element
    const gradientElement = GradientHelpers.getGradientById(this.gradientId);
    // Object representing the SVG element
    const gradient = this.isLinear ?
      new LinearGradient(gradientElement) :
      new RadialGradient(gradientElement);
    gradient.stops[index][propertyType] = event.target.value;
    // Update SVG
    gradient.stops.forEach((stop, i) => {
      stop.toElement(gradientElement.childNodes[i]);
    });
  },

  onSvgMouseDown: function (event) {
    this.mouseDown = true;
  },

  onSvgMouseMove: function (event) {
    if (!this.mouseDown) return;

    // Id of selected circle in gradient direction editor
    let selectedCircleId = '';
    this.$el[0].querySelectorAll('.gradients-editor-dialog circle').forEach(circle => {
      const cx = circle.getAttribute('cx');
      const cy = circle.getAttribute('cy');
      // If cursor is close enough (32 pixels) to center of a circle, select it
      if (Math.abs(cx - event.offsetX) < 32 && Math.abs(cy - event.offsetY) < 32) {
        selectedCircleId = circle.id;
      }
    });

    if (selectedCircleId) {
      // x cannot be less than padding
      let x = Math.max(event.offsetX, this.padding);
      // x should not exceed padding on right
      x = Math.min(x, this.screenSize - this.padding);

      let y = Math.max(event.offsetY, this.padding);
      y = Math.min(y, this.screenSize - this.padding);

      const delta = this.screenSize - 2 * this.padding;

      // Gradient direction is defined in percentage
      let targetX = (x - this.padding) * 100 / delta;
      let targetY = (y - this.padding) * 100 / delta;

      if (event.shiftKey) {
        const snapPoints = [
          [0, 0], [0, 50], [0, 100],
          [50, 0], [50, 50], [50, 100],
          [100, 0], [100, 50], [100, 100]
        ];
        for (let i = 0; i < snapPoints.length; i++) {
          const snapPoint = snapPoints[i];
          if (Math.abs(targetX - snapPoint[0]) < 16 && Math.abs(targetY - snapPoint[1]) < 16) {
            targetX = snapPoint[0];
            targetY = snapPoint[1];
            break;
          }
        }
      }

      if (this.isLinear) {
        const isEndSelected = selectedCircleId === 'end';
        // Modify SVG gradient
        this.updateGradientDirection(targetX, targetY, isEndSelected);
        // Update UI for the recent changes
        this.updateGradientDirectionUi(targetX, targetY, isEndSelected);
      } else {
        // Modify SVG gradient
        this.updateGradientCenter(targetX, targetY);
        // Update UI for the recent changes
        this.updateGradientCenterUi(targetX, targetY);
      }
    }
  },

  onSvgMouseUp: function (event) {
    this.mouseDown = false;
  },

  onSvgMouseLeave: function (event) {
    this.mouseDown = false;
  }
});
